import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeartbeat, faUserMd, faHospital, faAward, faHandHoldingMedical, faUsers } from '@fortawesome/free-solid-svg-icons';
import './About.css';

const About = () => {
  const [animatedElements, setAnimatedElements] = useState(new Set());
  const aboutUsRef = useRef(null);
  const missionVisionRef = useRef(null);
  const valuesRef = useRef(null);
  const teamRef = useRef(null);
  const facilityRef = useRef(null);
  const communityRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const elementId = entry.target.id;
          setAnimatedElements(prev => new Set([...prev, elementId]));
          
          // Add staggered animation to child elements
          const children = entry.target.querySelectorAll('.animate-on-scroll, .animate-left, .animate-right, .animate-scale');
          children.forEach((child, index) => {
            setTimeout(() => {
              child.classList.add('animate');
            }, index * 100);
          });
        }
      });
    }, observerOptions);

    // Observe sections
    const sections = [aboutUsRef.current, missionVisionRef.current, valuesRef.current, teamRef.current, facilityRef.current, communityRef.current, ctaRef.current];
    sections.forEach(section => {
      if (section) observer.observe(section);
    });
    
    return () => {
      observer.disconnect();
    };
  }, []);
  return (
    <div className="about-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <h1>About Tea Gardens GP</h1>
          <p>Bringing decades of combined medical experience to our community with a fresh, patient-centered approach</p>
        </div>
      </div>

      {/* About Us */}
      <section id="about-us" ref={aboutUsRef} className="section about-us">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="about-content">
                <div className="section-title text-left animate-left">
                  <h2>Our Story</h2>
                </div>
                <p className="animate-left animate-delay-1">Three highly experienced GP colleagues—Dr  Ahmed Shetia, Dr Mohammed Mohammed, and Dr Shaimaa  El-Gayar—began their medical journeys together during their training at Hunter New England Hospitals. Following this formative experience, each went on to serve the communities of Cessnock and Medowie as dedicated GPs.</p>
                <p className="animate-left animate-delay-2">United by their shared vision of patient-centered care, they reunited to create a new General Practice in the Tea Gardens area. Their aim: to bring exceptional, locally focused general practitioners to the community they love.</p>
                <p className="animate-left animate-delay-3">Together, Dr  Shetia, Dr Mohammed, and Dr  El-Gayar channel their collective expertise and longstanding connection to the region into a warm and trusted medical practice—committed to delivering accessible, high-quality primary care for residents of Tea Gardens and surrounding areas.</p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="about-image animate-right">
                <img src="/images/story.jpg?v=1" alt="Tea Gardens GP Clinic" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission & Vision */}
      <section id="mission-vision" ref={missionVisionRef} className="section mission-vision">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="mission-box animate-left">
                <div className="icon-box">
                  <FontAwesomeIcon icon={faHeartbeat} />
                </div>
                <h3>Our Mission</h3>
                <p>To provide compassionate, comprehensive, and high-quality general practice services that improve the health and wellbeing of our patients and community. We are committed to treating each patient with dignity, respect, and personalized attention, ensuring that their general practice needs are met with the highest standards of medical care.</p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="vision-box animate-right">
                <div className="icon-box">
                  <FontAwesomeIcon icon={faHospital} />
                </div>
                <h3>Our Vision</h3>
                <p>
                To be the leading general practice provider in the region, recognized for excellence in patient care, innovation in service delivery, and commitment to community health. We aspire to create an environment where patients feel valued, understood, and empowered to take an active role in their health journey.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section id="values" ref={valuesRef} className="section our-values">
        <div className="container">
          <div className="section-title animate-on-scroll">
            <h2>Our Core Values</h2>
            <p>The principles that guide our practice and patient care</p>
          </div>

          <div className="values-grid">
            <div className="value-box animate-scale animate-delay-1">
              <div className="value-icon">
                <FontAwesomeIcon icon={faUserMd} />
              </div>
              <h3>Excellence</h3>
              <p>We are committed to providing the highest standard of medical care through continuous learning, improvement, and adherence to best practices.</p>
            </div>

            <div className="value-box animate-scale animate-delay-2">
              <div className="value-icon">
                <FontAwesomeIcon icon={faHandHoldingMedical} />
              </div>
              <h3>Compassion</h3>
              <p>We approach each patient with empathy, understanding, and genuine care, recognizing the human aspect of general practice.</p>
            </div>

            <div className="value-box animate-scale animate-delay-3">
              <div className="value-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <h3>Respect</h3>
              <p>We honor the dignity, diversity, and individual needs of each patient, staff member, and general practice partner.</p>
            </div>

            <div className="value-box animate-scale animate-delay-4">
              <div className="value-icon">
                <FontAwesomeIcon icon={faAward} />
              </div>
              <h3>Integrity</h3>
              <p>We uphold the highest ethical standards in all our actions and decisions, fostering trust and confidence in our care.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section id="team" ref={teamRef} className="section our-team">
        <div className="container">
          <div className="section-title animate-on-scroll">
            <h2>Our General Practice Team</h2>
            <p>Meet the dedicated professionals committed to your health and wellbeing</p>
          </div>

          <div className="team-description">
            <p className="animate-on-scroll animate-delay-1">At Tea Gardens GP, we are proud of our diverse team of general practice professionals who bring a wealth of experience, expertise, and compassion to our practice. Our team includes general practitioners, nurses, allied health professionals, and administrative staff, all working together to provide comprehensive care that addresses the physical, emotional, and social aspects of health.</p>
            <p className="animate-on-scroll animate-delay-2">Our doctors have extensive training in various areas of medicine, allowing us to offer specialized care in fields such as women's health, men's health, pediatrics, geriatrics, chronic disease management, and preventive medicine. Our nursing staff are highly skilled in clinical procedures, patient education, and care coordination, ensuring that your general practice journey is seamless and supportive.</p>
            <p className="animate-on-scroll animate-delay-3">We believe that the strength of our team lies not only in our individual expertise but also in our collaborative approach to general practice. By working together and with our patients, we can achieve the best possible health outcomes for our community.</p>
            <div className="text-center mt-4">
              <Link to="/doctors" className="btn btn-primary">Meet Our Team</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Facility */}
      <section id="facility" ref={facilityRef} className="section our-facility">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="facility-image animate-left">
                <img src="/images/imgTwo.jpg" alt="Tea Gardens GP Facility" />
              </div>
            </div>
            <div className="col-lg-6">
              <div className="facility-content">
                <div className="section-title text-left animate-right">
                  <h2>Our Facility</h2>
                </div>
                <p className="animate-right animate-delay-1">Tea Gardens GP is housed in a modern, purpose-built medical facility designed to provide a comfortable and efficient general practice environment. Our clinic features spacious consultation rooms equipped with the latest medical technology, ensuring that our general practice providers have the tools they need to deliver exceptional care.</p>
                <p className="animate-right animate-delay-2">We understand that visiting a medical practice can sometimes be stressful, which is why we've created a welcoming and calming atmosphere throughout our facility. Our reception and waiting areas are designed with your comfort in mind, and our practice is fully accessible to patients with mobility challenges.</p>
                <p className="animate-right animate-delay-3">In addition to our consultation rooms, our facility includes treatment rooms for minor procedures, a well-equipped nursing station, pathology collection services, and dedicated spaces for allied health services. We continually invest in our infrastructure and technology to enhance the quality and efficiency of the care we provide.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Involvement */}
      <section id="community" ref={communityRef} className="section community-involvement">
        <div className="container">
          <div className="section-title animate-on-scroll">
            <h2>Community Involvement</h2>
            <p>Our commitment extends beyond our clinic walls</p>
          </div>

          <div className="community-content animate-on-scroll">
            <p>At Tea Gardens GP, we believe that being a general practice provider means being an active participant in the health and wellbeing of our entire community. As we establish our practice, we are committed to developing community initiatives and programs aimed at promoting health awareness, education, and access to care.</p>
            <p>Our planned community involvement includes:</p>
            <ul className="community-list">
              <li>
                <strong>Health Education Workshops:</strong> Regular sessions on important health topics such as diabetes management, heart health, mental wellbeing, and preventive care.
              </li>
              <li>
                <strong>School Health Programs:</strong> Partnerships with local schools to provide health education, screenings, and support for student wellbeing.
              </li>
              <li>
                <strong>Community Health Screenings:</strong> Free or low-cost health screenings at community events to promote early detection and prevention.
              </li>
              <li>
                <strong>Support for Local Health Initiatives:</strong> Collaboration with local organizations on projects that address specific health needs in our community.
              </li>
              <li>
                <strong>Medical Student Training:</strong> Participation in training the next generation of general practice professionals through student placements and mentorship.
              </li>
            </ul>
            <p>We are committed to being a positive force for health in our community and welcome opportunities to collaborate with local organizations and individuals who share our vision for a healthier future.</p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section id="cta" ref={ctaRef} className="section cta-section">
        <div className="container">
          <div className="cta-content animate-on-scroll">
            <h2>Experience the Tea Gardens GP Difference</h2>
            <p>Join our practice today and discover general practice care that truly cares about you and your family.</p>
            <Link to="https://www.hotdoc.com.au/medical-centres/tea-gardens-NSW-2324/tea-gardens-gp/doctors" className="btn-primary">Book an Appointment</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
